import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import ProfileCaptureCard from '../components/ProfileCaptureCard';
import ProfileCropper from '../components/ProfileCropper';
import CropperErrorBoundary from '../components/CropperErrorBoundary';
import ProfileMatchResults from '../components/ProfileMatchResults';
import ManageProfiles from './ManageProfiles';
import SubNav from '../components/ui/SubNav';
import { colors } from '../theme/tokens';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Client-side resize: keep file small (max 1280px on the long edge) to make
// upload snappy on mobile. Falls back to the original blob if anything fails.
const compressImage = async (file, maxDim = 1280, quality = 0.85) => {
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });

    let { width, height } = img;
    if (width <= maxDim && height <= maxDim) {
      return file; // already small enough
    }
    const scale = maxDim / Math.max(width, height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;
    return new File([blob], (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg', {
      type: 'image/jpeg'
    });
  } catch (err) {
    console.warn('compressImage failed, sending original:', err);
    return file;
  }
};

const VALID_PM_TABS = ['capture', 'contribute', 'manage'];

const ProfileMatch = () => {
  const { token, canApprove, canAddData } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();

  // Active sub-tab is read from the URL (/profile-match/:tab) so a reload
  // keeps the user on the same sub-section.
  const tab = VALID_PM_TABS.includes(tabParam) ? tabParam : 'capture';
  const setTab = (next) => navigate(`/profile-match/${next}`);

  const [rawFile, setRawFile] = useState(null);      // straight from camera/picker
  const [croppedFile, setCroppedFile] = useState(null); // after the user confirms a crop
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [lastMatchAt, setLastMatchAt] = useState(null);
  const [matchError, setMatchError] = useState(null);

  const handleMatch = useCallback(async () => {
    if (!croppedFile) {
      showToast('Crop the photo first', 'warning');
      return;
    }
    setBusy(true);
    setMatchError(null);
    try {
      const small = await compressImage(croppedFile);
      const fd = new FormData();
      fd.append('photo', small);

      const res = await fetch(`${API_URL}/api/profiles/match`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });

      const data = await res.json();
      if (!res.ok) {
        setMatchError(data.detail || data.error || 'Match failed');
        setResults(null);
        showToast(data.error || 'Match failed', 'error');
        return;
      }

      setResults(data.results || []);
      setLastMatchAt(new Date());
      if (!data.results || data.results.length === 0) {
        showToast('No matches found yet — add reference images to train the catalogue', 'info');
      } else {
        showToast(`${data.results.length} match${data.results.length === 1 ? '' : 'es'} found`, 'success');
      }
    } catch (err) {
      console.error('match request error:', err);
      setMatchError(err.message);
      showToast('Network error', 'error');
    } finally {
      setBusy(false);
    }
  }, [croppedFile, token, showToast]);

  const sendFeedback = async (profileId, outcome) => {
    try {
      await fetch(`${API_URL}/api/profiles/match/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ profileId, outcome })
      });
    } catch (err) {
      console.warn('feedback failed:', err);
    }
  };

  const promoteQueryAsTrainingData = useCallback(async (profileId) => {
    if (!croppedFile || !profileId) return;
    try {
      const fd = new FormData();
      fd.append('photo', croppedFile);
      const res = await fetch(`${API_URL}/api/profiles/${profileId}/references/from-match`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.warn('promote-to-training failed:', data.error || res.status);
        return;
      }
      showToast('Photo added to training queue', 'info');
    } catch (err) {
      console.warn('promote-to-training error:', err);
    }
  }, [croppedFile, token, showToast]);

  const handleConfirm = (r) => {
    sendFeedback(r.profileId, 'confirmed');
    showToast(`Confirmed: ${r.profileCode}`, 'success');
    promoteQueryAsTrainingData(r.profileId);
  };
  const handleWrong = (r) => {
    sendFeedback(r.profileId, 'wrong_match');
    showToast(`Marked ${r.profileCode} as wrong`, 'info');
  };
  const handleNoneOfThese = () => {
    sendFeedback(null, 'none_of_these');
    showToast('Feedback recorded — admin will be notified', 'info');
  };

  const pmTabs = [
    { key: 'capture', label: 'Capture', icon: 'photo_camera', active: tab === 'capture', onClick: () => setTab('capture') },
    ...(canAddData ? [{ key: 'contribute', label: 'Contribute training image', icon: 'add_a_photo', active: tab === 'contribute', onClick: () => setTab('contribute') }] : []),
    ...(canApprove ? [{ key: 'manage', label: 'Manage / approve', icon: 'build', active: tab === 'manage', onClick: () => setTab('manage') }] : [])
  ];

  return (
    <div style={styles.page}>
      <SubNav items={pmTabs} />

      <div style={styles.content}>
        {tab === 'capture' && (
          <div style={styles.captureCol}>
            {!rawFile && (
              <ProfileCaptureCard
                onCapture={(f) => { setRawFile(f); setCroppedFile(null); setResults(null); setMatchError(null); }}
                busy={busy}
              />
            )}

            {rawFile && !croppedFile && (
              <CropperErrorBoundary onReset={() => { setRawFile(null); setCroppedFile(null); }}>
                <ProfileCropper
                  file={rawFile}
                  onConfirm={(cropped) => setCroppedFile(cropped)}
                  onCancel={() => { setRawFile(null); setCroppedFile(null); }}
                />
              </CropperErrorBoundary>
            )}

            {croppedFile && (
              <CroppedPreview
                file={croppedFile}
                busy={busy}
                onRecrop={() => setCroppedFile(null)}
                onRetake={() => { setRawFile(null); setCroppedFile(null); setResults(null); }}
              />
            )}

            <button
              style={{ ...styles.analyseBtn, opacity: !croppedFile || busy ? 0.5 : 1 }}
              disabled={!croppedFile || busy}
              onClick={handleMatch}
            >
              {busy ? 'Analysing…' : '🔎 Find matches'}
            </button>

            {matchError && (
              <div style={styles.errorBox}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Matcher unavailable</div>
                <div style={{ fontSize: 13 }}>{matchError}</div>
              </div>
            )}

            {results && (
              <ProfileMatchResults
                results={results}
                onConfirm={handleConfirm}
                onWrong={handleWrong}
                onNoneOfThese={handleNoneOfThese}
                token={token}
                apiUrl={API_URL}
              />
            )}

            {lastMatchAt && results && results.length === 0 && (
              <div style={styles.empty}>
                No results. Either the catalogue has no approved references yet, or the matcher
                service isn't installed. Ask an admin.
              </div>
            )}
          </div>
        )}

        {tab === 'contribute' && <ContributeReference token={token} showToast={showToast} />}

        {tab === 'manage' && canApprove && <ManageProfiles />}
      </div>
    </div>
  );
};

// ---------- Cropped-preview card shown right before "Find matches" ----------
const CroppedPreview = ({ file, busy, onRecrop, onRetake }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div style={{ background: colors.glass, border: `1px solid ${colors.glassBorder}`, borderRadius: 14, padding: 12, color: colors.text }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Cropped square ready</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {url && <img src={url} alt="crop" style={{ width: 120, height: 120, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />}
        <div style={{ fontSize: 13, color: colors.textMuted, flex: 1 }}>
          1024×1024 JPEG · {(file.size / 1024).toFixed(0)} KB
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" onClick={onRecrop} disabled={busy}
              style={{ padding: '7px 14px', background: colors.glass, color: colors.text, border: `1px solid ${colors.glassBorder}`, borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Re-crop
            </button>
            <button type="button" onClick={onRetake} disabled={busy}
              style={{ padding: '7px 14px', background: colors.glass, color: colors.text, border: `1px solid ${colors.glassBorder}`, borderRadius: 999, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Retake photo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Contribute reference image (Gold+ sales) ----------
const ContributeReference = ({ token, showToast }) => {
  const [profiles, setProfiles] = useState([]);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [source, setSource] = useState('photo');
  const [rawFile, setRawFile] = useState(null);
  const [file, setFile] = useState(null);   // cropped output ready to upload
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/profiles?limit=30${q ? `&q=${encodeURIComponent(q)}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (active) setProfiles(data.items || []);
      } catch (e) { console.error(e); }
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [q, token]);

  const submit = async () => {
    if (!selected || !file) {
      showToast('Pick a profile and an image', 'warning');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('source', source);
      const res = await fetch(`${API_URL}/api/profiles/${selected._id}/references`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (!res.ok) {
        showToast(data.error || 'Submit failed', 'error');
      } else {
        showToast('Submitted! Pending admin approval', 'success');
        setFile(null);
        setRawFile(null);
      }
    } catch (e) {
      showToast('Network error', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640, color: colors.text }}>
      <h3 style={{ margin: 0 }}>Contribute a training image</h3>
      <div style={{ color: colors.textMuted, fontSize: 14 }}>
        Pick the matching profile, attach a clean photo or drawing, and submit. An admin will
        approve it before it's used for matching.
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search profile code or name…"
        style={styles.searchInput}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, maxHeight: 240, overflowY: 'auto', border: `1px solid ${colors.glassBorder}`, borderRadius: 10, padding: 8 }}>
        {profiles.map(p => (
          <button
            key={p._id}
            onClick={() => setSelected(p)}
            style={{
              textAlign: 'left',
              padding: 8,
              border: selected && selected._id === p._id ? `2px solid ${colors.parGreen}` : `1px solid ${colors.glassBorder}`,
              background: selected && selected._id === p._id ? 'rgba(47,182,92,0.16)' : colors.glass,
              color: colors.text,
              borderRadius: 8,
              cursor: 'pointer'
            }}
          >
            <div style={{ fontWeight: 700, color: colors.parGreenBright }}>{p.profileCode}</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>{p.name}</div>
          </button>
        ))}
        {profiles.length === 0 && <div style={{ color: colors.textMuted, padding: 8 }}>No profiles match.</div>}
      </div>

      <div>
        <label style={{ marginRight: 12 }}>Source:</label>
        {['photo', 'drawing', 'cad'].map(s => (
          <label key={s} style={{ marginRight: 8 }}>
            <input
              type="radio"
              checked={source === s}
              onChange={() => setSource(s)}
            />{' '}
            {s}
          </label>
        ))}
      </div>

      {!rawFile && !file && (
        <label style={{ ...styles.analyseBtn, display: 'inline-block', textAlign: 'center', opacity: !selected ? 0.5 : 1 }}>
          📷 Take / choose photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={!selected}
            onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) setRawFile(f); }}
            style={{ display: 'none' }}
          />
        </label>
      )}

      {rawFile && !file && (
        <CropperErrorBoundary onReset={() => { setRawFile(null); setFile(null); }}>
          <ProfileCropper
            file={rawFile}
            onConfirm={(cropped) => setFile(cropped)}
            onCancel={() => setRawFile(null)}
          />
        </CropperErrorBoundary>
      )}

      {file && (
        <CroppedPreview
          file={file}
          busy={busy}
          onRecrop={() => setFile(null)}
          onRetake={() => { setFile(null); setRawFile(null); }}
        />
      )}

      <button
        disabled={busy || !file || !selected}
        onClick={submit}
        style={{ ...styles.analyseBtn, opacity: busy || !file || !selected ? 0.5 : 1 }}
      >
        {busy ? 'Submitting…' : '⬆️ Submit for approval'}
      </button>
    </div>
  );
};

const styles = {
  page: { padding: '4px 12px 16px', maxWidth: 1100, margin: '0 auto', color: colors.text },
  content: {},
  captureCol: { display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: 720, margin: '0 auto' },
  analyseBtn: {
    padding: '14px',
    border: 'none',
    background: 'linear-gradient(135deg, #34d36b 0%, #2fb65c 55%, #1d8c3a 100%)',
    color: '#fff',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer'
  },
  errorBox: {
    background: 'rgba(229,84,75,0.12)',
    color: colors.red,
    padding: 12,
    borderRadius: 10,
    border: '1px solid rgba(229,84,75,0.35)'
  },
  empty: { color: colors.textMuted, padding: 12, textAlign: 'center' },
  searchInput: {
    flex: 1,
    padding: '10px 12px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    fontSize: 14,
    background: colors.glass,
    color: colors.text
  }
};

export default ProfileMatch;
